/* eslint-disable react/jsx-props-no-spreading */
import {
  DataGridPremium as MuiDataGridPremium,
  DataGridPremiumProps as MuiDataGridPremiumProps,
  GridColDef,
  GridColumnResizeParams,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';
import React, { useCallback, useMemo, useState } from 'react';

// Read column widths once from sessionStorage. Returns an empty map on any failure
// (parse error, quota disabled, SSR) so the grid still renders correctly.
// sessionStorage scopes to the browser tab session — cleared when the tab closes,
// so each new session starts with the grid's default widths.
const loadStoredColumnWidths = (key: string): Record<string, number> => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return {};
    const stored = window.sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

export interface DataGridProps<R extends GridValidRowModel = any> extends MuiDataGridPremiumProps<R> {
  /**
   * When set, the grid persists column widths to sessionStorage under this key.
   * Pick a unique value per grid so multiple grids don't share state.
   *
   * The persistence model is declarative: stored widths are spliced into the
   * column defs each render (`{ ...column, width: storedWidth, flex: undefined }`),
   * which means whatever MUI internally resets the column dimensions to, it
   * resets to the stored width because that's what the column def says.
   * https://github.com/mui/mui-x/issues/14452
   *
   * Persistence is session-scoped: widths survive across page navigations
   * within the same tab but reset when the tab is closed or a new tab is
   * opened.
   */
  columnWidthStorageKey?: string;
}

function DataGrid<R extends GridValidRowModel = any>({
  columnWidthStorageKey,
  columns,
  onColumnWidthChange,
  ...rest
}: DataGridProps<R>) {
  // Hooks are called unconditionally; the persistence behavior is gated below.
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() =>
    columnWidthStorageKey ? loadStoredColumnWidths(columnWidthStorageKey) : {}
  );

  const handleColumnWidthChange = useCallback(
    (params: GridColumnResizeParams, event: any, details: any) => {
      const field = params?.colDef?.field;
      const width = params?.width;
      if (columnWidthStorageKey && field && width) {
        setColumnWidths((prev) => {
          const next = { ...prev, [field]: width };
          try {
            sessionStorage.setItem(columnWidthStorageKey, JSON.stringify(next));
          } catch {
            // sessionStorage quota exceeded / disabled — state still wins for this session.
          }
          return next;
        });
      }
      onColumnWidthChange?.(params, event, details);
    },
    [columnWidthStorageKey, onColumnWidthChange]
  );

  // Splice the stored width per field. Clear `flex` when we have a stored width
  // so MUI uses our fixed value (flex wins over width when both are set).
  const persistedColumns: readonly GridColDef<R>[] | undefined = useMemo(() => {
    if (!columnWidthStorageKey || !columns) return columns;
    return columns.map((column) => {
      const storedWidth = columnWidths[column.field];
      return storedWidth ? { ...column, width: storedWidth, flex: undefined } : column;
    });
  }, [columns, columnWidths, columnWidthStorageKey]);

  return (
    <MuiDataGridPremium<R>
      {...rest}
      columns={persistedColumns}
      onColumnWidthChange={handleColumnWidthChange}
    />
  );
}

export default DataGrid;
